import { useCallback, useContext, useMemo } from "react";
import { isEqual } from "@common/helpers/comparison";
import { FiltersTypes } from "@common/types/filters/general";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { useBulkActions } from "@components/transactions/TransactionsBulkActions/useBulkActions";
import { useBulkSetScheduleOptions } from "@components/transactions/TransactionsBulkActions/useBulkSetScheduleOptions";
import { usePaginationWithBulkReset } from "@components/transactions/hooks/usePaginationWithBulkReset";
import {
    useDismissSuspiciousTransactionsMutation,
    useRestoreSuspiciousTransactionsMutation,
} from "@api/scheduleReview.api";
import { useScheduleReviewDismiss } from "./useScheduleReviewDismiss";
import { useSuspiciousConfirmAll } from "./useSuspiciousConfirmAll";
import styles from "./ScheduleReviewTaskActionBar.module.scss";

interface SuspiciousTransactionReviewTaskActionBarProps {
    entityId: number | null;
    initialFilters: FiltersTypes;
    scrollTo?: (options: ScrollToOptions) => void;
}

/**
 * Footer of the suspicious transactions task: pagination on the left unless
 * the table loads more on scroll;
 * Dismiss selected / Confirm selected while rows are selected, otherwise
 * Confirm all (or Confirm filtered once the user narrowed the task filters).
 * Confirming opens the Set schedule palette; the picked template is applied
 * exactly like the transactions page bulk action.
 */
export const SuspiciousTransactionReviewTaskActionBar = ({
    entityId,
    initialFilters,
    scrollTo,
}: SuspiciousTransactionReviewTaskActionBarProps) => {
    const {
        currentPage,
        totalPages,
        transactions,
        filters,
        transactionCount,
        loadMore,
    } = useContext(PaginatedTransactionsDataContext);
    const { setPage } = useContext(PaginatedTransactionsActionsContext);
    const bulkActions = useBulkActions();
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const viewOnlyTooltip = useViewOnlyTooltipReason("");
    const { open: openCommands } = useCommands();

    const { options: selectedOptions, confirmModal: scheduleConfirmModal } =
        useBulkSetScheduleOptions({
            isEnabled: true,
            isFieldLocked: false,
        });
    const { options: confirmAllOptions, isDraining } = useSuspiciousConfirmAll({
        entityId,
        filters,
    });

    const dismissMutation = useDismissSuspiciousTransactionsMutation();
    const restoreMutation = useRestoreSuspiciousTransactionsMutation();
    const dismissTransactions = useScheduleReviewDismiss<number>({
        dismiss: (transactionIds) =>
            dismissMutation.mutateAsync({
                entityId: entityId ?? 0,
                transactionIds,
            }),
        restore: (transactionIds) =>
            restoreMutation.mutateAsync({
                entityId: entityId ?? 0,
                transactionIds,
            }),
        noun: "transaction",
    });

    const onPageChange = useCallback(
        (newPage: number) => {
            setPage(newPage);
            scrollTo?.({ top: 0, behavior: "smooth" });
        },
        [setPage, scrollTo],
    );
    const pageChangeWithBulkActionReset = usePaginationWithBulkReset({
        onPageChange,
    });

    const haveFiltersChanged = useMemo(
        () => !isEqual(filters, initialFilters),
        [filters, initialFilters],
    );
    const hasSelection =
        bulkActions.selectedCount > 0 && !bulkActions.hasSelectedAll;

    const handleConfirmSelected = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        openCommands(CommandsDirectory.TRANSACTION_BULK_SCHEDULE, {
            transactionBulkScheduleOptions: selectedOptions,
            selectedTransactionCount: bulkActions.selectedCount,
        });
    }, [
        bulkActions.selectedCount,
        openCommands,
        refuseIfViewOnly,
        selectedOptions,
    ]);

    const handleConfirmAll = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        openCommands(CommandsDirectory.TRANSACTION_BULK_SCHEDULE, {
            transactionBulkScheduleOptions: confirmAllOptions,
            selectedTransactionCount: transactionCount,
        });
    }, [confirmAllOptions, openCommands, refuseIfViewOnly, transactionCount]);

    const handleDismissSelected = useCallback(async () => {
        if (entityId == null || refuseIfViewOnly()) {
            return;
        }
        const transactionIds = bulkActions.selectedArray.map(
            (transaction) => transaction.id,
        );
        bulkActions.clearSelection();
        await dismissTransactions(transactionIds);
    }, [bulkActions, dismissTransactions, entityId, refuseIfViewOnly]);

    if (!transactions || transactions.length === 0) {
        return null;
    }

    return (
        <>
            {scheduleConfirmModal}
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                {!loadMore && (
                    <Pagination
                        size="md"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={pageChangeWithBulkActionReset}
                    />
                )}
                <div className={styles.actions}>
                    {hasSelection ? (
                        <>
                            <Tooltip content={viewOnlyTooltip}>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={() => void handleDismissSelected()}
                                    disabled={
                                        isViewOnly || dismissMutation.isPending
                                    }
                                >
                                    Dismiss selected
                                </Button>
                            </Tooltip>
                            <Tooltip content={viewOnlyTooltip}>
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={handleConfirmSelected}
                                    disabled={
                                        isViewOnly ||
                                        selectedOptions.length === 0
                                    }
                                >
                                    Confirm selected
                                </Button>
                            </Tooltip>
                        </>
                    ) : (
                        <WithHotkey
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            tooltip={viewOnlyTooltip}
                            hotKeyOptions={{
                                description: haveFiltersChanged
                                    ? "Set a schedule on the filtered transactions"
                                    : "Set a schedule on all transactions",
                                preventDefault: true,
                            }}
                        >
                            {(hotkeyHint) => (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={handleConfirmAll}
                                    disabled={
                                        isViewOnly ||
                                        isDraining ||
                                        confirmAllOptions.length === 0
                                    }
                                    rightIcon={hotkeyHint}
                                >
                                    {haveFiltersChanged
                                        ? "Confirm filtered"
                                        : "Confirm all"}
                                </Button>
                            )}
                        </WithHotkey>
                    )}
                </div>
            </CommandIsland>
        </>
    );
};
